import React from 'react';

export default function InterpolationExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p>
        <strong>Goal:</strong> Construct a smooth function that exactly passes through a given set of data points
        <code> (xᵢ, yᵢ) </code> for  <code> i = 0,...,n </code>. Unlike regression,
        interpolation ensures the curve goes through every point.
      </p>

      <p className="mt-3"><strong>Mathematical explanation</strong></p>
      <ul className="list-disc pl-5 space-y-2 mb-4">
        <li>
          <strong>Polynomial interpolation:</strong> A single global polynomial of degree <code>n</code>
          that fits all <code> n + 1 </code>points.
          Suitable for small datasets. Can lead to oscillations for larger ones (Runge's phenomenon).
        </li>
        <li>
          <strong>Linear spline:</strong> Connects points by straight lines. Simple and fast, but not smooth.
        </li>
        <li>
          <strong>Cubic spline:</strong> Piecewise cubic polynomials fitted between each pair of points.
          Ensures continuity of the function and its first two derivatives.
        </li>
        <p className="mt-3"><strong>Cubic spline structure:</strong></p>
        <p className="mb-2">
          The spline consists of <code>n</code> cubic polynomials <code>Sᵢ(x)</code>, one for each interval <code>[xᵢ, xᵢ₊₁]</code>,
          where <code>i = 0, 1, ..., n - 1</code>.
          Each spline segment has the form:
        </p>

        <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 text-left font-mono leading-snug">
        Sᵢ(x) = aᵢ·(x − xᵢ)³ <br /> 
             {"   "}   + bᵢ·(x − xᵢ)²<br />
             {"   "}   + cᵢ·(x − xᵢ) + dᵢ
        </pre>

        <p>
          These coefficients are computed such that the following conditions hold:
        </p>
        <ul className="list-disc pl-5 space-y-1 mb-4">
          <li>
            Continuity of the function:
            <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">

              Sᵢ(xᵢ₊₁) = Sᵢ₊₁(xᵢ₊₁)
            </pre>
          </li>
          <li>
            Continuity of the first derivative:
            <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">

              Sᵢ′(xᵢ₊₁) = Sᵢ₊₁′(xᵢ₊₁)
            </pre>
          </li>
          <li>
            Continuity of the second derivative:
            <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">

              Sᵢ″(xᵢ₊₁) = Sᵢ₊₁″(xᵢ₊₁)
            </pre>
          </li>
        </ul>
        <p className="mt-3"><strong>Cubic spline boundary conditions:</strong></p>
        <ul className="list-disc pl-5 space-y-2 mb-4">
          <li>
            <strong>Natural:</strong> The second derivative at the endpoints is set to zero
            (<code>S''(x₀) = S''(xₙ) = 0</code>). This leads to a "free" or minimally curved spline at the edges.
          </li>
          <li>
            <strong>Clamped (complete):</strong> The first derivative at the endpoints is specified.
            This forces the slope of the spline to match known tangents at the boundaries,
            e.g. <code>S'(x₀) = a</code>, <code>S'(xₙ) = b</code> (here we simply use 0).
          </li>
          <li>
            <strong>Periodic:</strong> Assumes the function is periodic.
            The spline is constructed such that the function and its first two derivatives match at the endpoints: <code>S(x₀) = S(xₙ)</code>, <code>S'(x₀) = S'(xₙ)</code>, <code>S''(x₀) = S''(xₙ)</code>.
            Use this if the underlying data is cyclic in nature.
          </li>
        </ul>

      </ul>

      <p className="mt-4"><strong>Input format</strong></p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li>Enter points as an 2D-array of <code>[x, y]</code> pairs:</li>
        <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1">
          [[0, 1], [1, 2], [2, 1.5], <br /> [3, 4.0]]
        </pre>
        <li>At least two points required.</li>
        <li>For spline interpolation: x-values must be strictly increasing.</li>
      </ul>

      <p className="mt-4"><strong>Output format</strong></p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li>A visual plot of the interpolated function.</li>
        <li>Exact symbolic form of the interpolating polynomial together with the coefficient vector.</li>
        <li>Piecewise expressions for splines, showing each segment's formula and interval.</li>
      </ul>


    </div>
  );
}
